// ADDED: the Multipacks page. Same shape as the Models page, for sealed packs of several
// cars. A pack is catalogued as one item with a piece count; the castings inside it are
// not recorded, so packs are counted separately from loose cars everywhere in the app.
import React, { useState, useEffect, useCallback } from 'react';
import ReactPaginate from 'react-paginate';

import axios, { getErrorMessage } from '../../api/axios';
import NewMultipackForm from './NewMultipackForm';
// ADDED: the packs visibility switch reads and writes the signed-in user
import { useAuth } from '../Components/AuthProvider';

export default function Multipacks() {
    // ADDED: multipacks hang off the User rather than a Collection, so they have no
    // collection toggle to inherit. This one switch is their equivalent: off by default,
    // and when on every collector on your friends list can see your packs.
    const { user, updateUser } = useAuth();

    const handleTogglePacksVisibility = async () => {
        const nextValue = !user?.multipacksPublic;
        try {
            const response = await axios.put('/me/multipacks-visibility', { multipacksPublic: nextValue });
            updateUser(response.data.user);
        } catch (err) {
            console.error(getErrorMessage(err, 'Could not change who can see your packs.'));
        }
    };

    const [multipacks, setMultipacks] = useState([]);
    const [filterOptions, setFilterOptions] = useState({ themes: [], pieceCounts: [], years: [] });
    const [selectedFilters, setSelectedFilters] = useState({ theme: 'all', pieceCount: 'all', year: 'all' });
    const [sort, setSort] = useState('AZ');

    const [searchInput, setSearchInput] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');

    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const itemsPerPage = 24;

    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');
    const [isCreateVisible, setIsCreateVisible] = useState(false);
    const [showFilters, setShowFilters] = useState(false);

    // Same debounce the Models page uses, so typing does not fire a request per keystroke
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(searchInput);
            setCurrentPage(1);
        }, 400);
        return () => clearTimeout(timer);
    }, [searchInput]);

    useEffect(() => {
        axios.get('/multipacks/filters')
            .then((response) => setFilterOptions(response.data))
            .catch((err) => console.error(err)); // a failed filter load should not block the grid
    }, []);

    const fetchMultipacks = useCallback(async () => {
        setIsLoading(true);
        try {
            const response = await axios.get('/multipacks', {
                params: {
                    page: currentPage,
                    limit: itemsPerPage,
                    search: debouncedSearch,
                    theme: selectedFilters.theme,
                    pieceCount: selectedFilters.pieceCount,
                    year: selectedFilters.year,
                    sort,
                },
            });
            setMultipacks(response.data.multipacks);
            setTotalPages(response.data.totalPages);
            setError('');
        } catch (err) {
            setError(getErrorMessage(err, 'Could not load the multipacks.'));
        } finally {
            setIsLoading(false);
        }
    }, [currentPage, debouncedSearch, selectedFilters, sort]);

    useEffect(() => { fetchMultipacks(); }, [fetchMultipacks]);

    // The owned counter on a card. Zero clears the row on the server.
    const handleOwnedChange = async (multipackId, nextQuantity) => {
        if (nextQuantity < 0 || nextQuantity > 999) return;
        try {
            const response = await axios.put(`/multipacks/${multipackId}/owned`, { quantity: nextQuantity });
            setMultipacks((current) => current.map((pack) => (
                pack.id === multipackId ? { ...pack, ownedQuantity: response.data.quantity } : pack
            )));
        } catch (err) {
            setError(getErrorMessage(err, 'Could not update how many you own.'));
        }
    };

    const handleFilterChange = (key, value) => {
        setSelectedFilters((previous) => ({ ...previous, [key]: value }));
        setCurrentPage(1);
    };

    // MODIFIED: filter ids are prefixed so they cannot collide with the create
    // form's inputs, which reuse names like "model", "series" and "theme".
    const filterFields = [
        { key: 'theme', label: 'Package series', options: filterOptions.themes },
        { key: 'pieceCount', label: 'Cars per pack', options: filterOptions.pieceCounts },
        { key: 'year', label: 'Year', options: filterOptions.years },
    ];

    return (
        <>
            <section className="cv-page pt-8 pb-6 px-4 sm:px-8">
                <div className="max-w-6xl mx-auto text-center space-y-2">
                    <p className="cv-eyebrow">The catalog</p>
                    <h1 className="cv-display text-4xl sm:text-5xl">Multipacks</h1>
                    <p className="text-sm cv-muted max-w-xl mx-auto">
                        Sealed packs of 3, 5, 10 or more. Counted separately from your loose cars,
                        since what is inside a sealed pack is not catalogued.
                    </p>

                    {/* ADDED: packs have no per-collection toggle to inherit, so this single
                        switch decides whether friends see them at all. */}
                    <button
                        type="button"
                        onClick={handleTogglePacksVisibility}
                        aria-pressed={Boolean(user?.multipacksPublic)}
                        className="inline-flex items-center gap-3 rounded-xl px-3 py-2 text-sm mt-1 transition duration-300"
                        style={{ border: '1px solid var(--border-subtle)' }}>
                        <span className={user?.multipacksPublic ? 'cv-strong' : 'cv-muted'}>
                            {user?.multipacksPublic ? 'Friends can see my packs' : 'My packs are private'}
                        </span>
                        <span className="relative w-10 h-5 rounded-full shrink-0 transition duration-300"
                              style={{ backgroundColor: user?.multipacksPublic ? 'var(--accent)' : 'var(--border-subtle)' }}>
                            <span className="absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-all duration-300"
                                  style={{ left: user?.multipacksPublic ? '1.375rem' : '0.125rem' }} />
                        </span>
                    </button>
                </div>
            </section>

            <section className="cv-page grid grid-cols-1 lg:grid-cols-4 gap-5 px-4 sm:px-8 pb-8 max-w-6xl mx-auto w-full">
                {/* Filters: collapsible below lg, always open from lg up */}
                <div className="cv-card lg:col-span-1 h-fit overflow-hidden lg:sticky lg:top-24">
                    <button onClick={() => setShowFilters(!showFilters)}
                        aria-expanded={showFilters}
                        className="lg:hidden w-full flex justify-between items-center px-4 py-3 cv-accent font-display font-semibold uppercase tracking-wider text-sm">
                        <span>Filters</span>
                        <span className="text-lg leading-none">{showFilters ? '−' : '+'}</span>
                    </button>

                    <div className={showFilters ? 'block' : 'hidden lg:block'}>
                        <div className="px-4 pb-4 pt-2 lg:pt-4 space-y-3">
                            <h2 className="cv-display text-lg hidden lg:block">Filters</h2>
                            {filterFields.map((field) => (
                                <div key={field.key}>
                                    <label htmlFor={`filter-${field.key}`} className="cv-label">{field.label}</label>
                                    <select id={`filter-${field.key}`} value={selectedFilters[field.key]}
                                        onChange={(e) => handleFilterChange(field.key, e.target.value)}
                                        className="cv-input">
                                        <option value="all">All</option>
                                        {field.options.map((option) => (
                                            <option key={option} value={option}>
                                                {field.key === 'pieceCount' ? `${option} cars` : option}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            ))}
                            <button
                                onClick={() => { setSelectedFilters({ theme: 'all', pieceCount: 'all', year: 'all' }); setCurrentPage(1); }}
                                className="text-sm cv-accent underline pt-1">
                                Clear filters
                            </button>
                        </div>
                    </div>
                </div>

                <div className="lg:col-span-3 space-y-5">
                    <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                        <div className="relative w-full">
                            <input
                                value={searchInput}
                                onChange={(e) => setSearchInput(e.target.value)}
                                type="text" autoComplete="off"
                                placeholder="Search for a pack"
                                className="cv-input rounded-full pl-11"
                            />
                            <div className="absolute inset-y-0 left-0 flex items-center pl-4 pointer-events-none">
                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 cv-muted">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z" />
                                </svg>
                            </div>
                        </div>

                        <div className="flex items-center gap-2 shrink-0">
                            <button onClick={() => setIsCreateVisible(true)} className="cv-btn cv-btn-outline whitespace-nowrap">
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                                    <path d="M10.75 4.75a.75.75 0 0 0-1.5 0v4.5h-4.5a.75.75 0 0 0 0 1.5h4.5v4.5a.75.75 0 0 0 1.5 0v-4.5h4.5a.75.75 0 0 0 0-1.5h-4.5v-4.5Z" />
                                </svg>
                                Create
                            </button>
                            <select value={sort} onChange={(e) => { setSort(e.target.value); setCurrentPage(1); }}
                                aria-label="Sort multipacks" className="cv-input w-auto py-2.5">
                                <option value="AZ">A-Z</option>
                                <option value="ZA">Z-A</option>
                                <option value="newest">Newest</option>
                                <option value="oldest">Oldest</option>
                                <option value="price">Price</option>
                                <option value="pieces">Most cars</option>
                            </select>
                        </div>
                    </div>

                    {error && <p className="cv-alert-error text-center">{error}</p>}
                    {isLoading && <p className="text-center cv-muted py-10">Loading multipacks...</p>}

                    {!isLoading && !error && multipacks.length === 0 &&
                        <div className="cv-card border-dashed p-8 text-center space-y-2">
                            <p className="cv-strong">No multipacks in the catalog yet.</p>
                            <p className="text-sm cv-muted">Use Create to add the first one.</p>
                        </div>
                    }

                    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
                        {multipacks.map((pack) => (
                            <div key={pack.id} className="cv-card cv-card-interactive cv-speedline flex flex-col">
                                <div className="p-4 flex-1">
                                    <div className="relative">
                                        <div className="cv-well w-full h-40 rounded-xl flex items-center justify-center overflow-hidden">
                                            {/* MODIFIED: the ~20KB preview. Lists no longer receive the full photo, which */}
                                            {/* pushed a page over the database's 5MB response limit. */}
                                            {pack.imageThumb
                                                ? <img src={pack.imageThumb} alt={pack.name} className="w-full h-full object-contain" />
                                                : <span className="text-xs cv-muted">No image</span>}
                                        </div>
                                        {/* The pack size, always visible - it is the defining fact about a pack */}
                                        <span className="absolute top-2 left-2 bg-brand-ink text-white font-display font-bold text-xs px-2 py-1 rounded-lg shadow">
                                            {pack.pieceCount} cars
                                        </span>
                                        {pack.ownedQuantity > 0 &&
                                            <span className="absolute top-2 right-2 bg-brand-flame text-white font-display font-bold text-xs px-2 py-1 rounded-lg shadow">
                                                <span aria-hidden="true">&times;{pack.ownedQuantity}</span>
                                                <span className="sr-only">You own {pack.ownedQuantity}</span>
                                            </span>
                                        }
                                    </div>

                                    <p className="text-[0.65rem] cv-accent uppercase tracking-widest mt-3 truncate">{pack.theme}</p>
                                    <h2 className="cv-title text-lg mt-1 break-words leading-snug">{pack.name}</h2>
                                    <p className="text-xs cv-muted mt-1">{pack.seriesId} &middot; {pack.datereleased}</p>
                                    {/* MODIFIED: an unpriced pack says so rather than showing $0.00 */}
                                    <p className="font-display font-bold cv-strong mt-2">
                                        {pack.price === null || pack.price === undefined
                                            ? <span className="cv-muted font-normal text-sm">Not priced</span>
                                            : `$${Number(pack.price).toFixed(2)}`}
                                    </p>
                                </div>

                                {/* Owned counter, the equivalent of adding a car to a collection */}
                                <div className="flex items-center justify-between px-4 py-2.5" style={{ borderTop: '1px solid var(--border-subtle)' }}>
                                    <span className="text-xs cv-muted">You own</span>
                                    <div className="flex items-center gap-1 rounded-lg" style={{ border: '1px solid var(--border-subtle)' }}>
                                        <button aria-label={`Decrease owned count of ${pack.name}`}
                                            onClick={() => handleOwnedChange(pack.id, (pack.ownedQuantity || 0) - 1)}
                                            disabled={!pack.ownedQuantity}
                                            className="w-8 h-8 flex items-center justify-center cv-muted hover:text-brand-flame disabled:opacity-40 transition duration-300">
                                            &minus;
                                        </button>
                                        <span className="font-display font-bold text-sm cv-strong w-6 text-center tabular-nums">
                                            {pack.ownedQuantity || 0}
                                        </span>
                                        <button aria-label={`Increase owned count of ${pack.name}`}
                                            onClick={() => handleOwnedChange(pack.id, (pack.ownedQuantity || 0) + 1)}
                                            className="w-8 h-8 flex items-center justify-center cv-muted hover:text-brand-flame transition duration-300">
                                            +
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {totalPages > 1 &&
                        <div className="flex justify-center pt-4">
                            <ReactPaginate
                                previousLabel={'←'} nextLabel={'→'} breakLabel={'...'}
                                pageCount={totalPages} forcePage={currentPage - 1}
                                marginPagesDisplayed={1} pageRangeDisplayed={2}
                                onPageChange={(data) => { setCurrentPage(data.selected + 1); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                                containerClassName={'flex flex-wrap justify-center gap-2 font-display font-semibold'}
                                pageClassName={'cv-btn cv-btn-quiet px-4 py-2 cursor-pointer'}
                                activeClassName={'!bg-brand-flame !text-white !border-brand-flame'}
                                previousClassName={'cv-btn cv-btn-outline px-4 py-2 cursor-pointer'}
                                nextClassName={'cv-btn cv-btn-outline px-4 py-2 cursor-pointer'}
                                disabledClassName={'opacity-50 cursor-not-allowed'}
                            />
                        </div>
                    }
                </div>
            </section>

            <NewMultipackForm
                isVisible={isCreateVisible}
                onClose={() => setIsCreateVisible(false)}
                onCreated={fetchMultipacks}
            />
        </>
    );
}
